import React, { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from "@mui/icons-material";
import WebsiteStatusResult from "./Results/WebsiteStatusResult";
import WebPresenceResult from "./Results/WebPresenceResult";
import NotificationsExportForm from "./NotificationsExportForm";
import NotificationDismissalForm from "./NotificationDismissalForm";
import { tzAwareHumanizeTimeString } from "../../utilities/formatting";
import APIPagedNotifications from "../../types/APIPagedNotifications";
import APIEnquiry from "../../types/APIEnquiry";
import { useTrueBizApi } from "../../api";
import APINotification, {
  APINotificationType,
} from "../../types/APINotification";
import { APIMonitorType } from "../../types/APIMonitor";
import { PendingNotificationsCountContext } from "./index";

const RESULTS_PER_PAGE = 15;

export default function MonitoringNotificationsTable({
  setPendingNotificationCount,
}: {
  setPendingNotificationCount: React.Dispatch<
    React.SetStateAction<number | null>
  >;
}) {
  const pendingNotificationCount = useContext(PendingNotificationsCountContext);
  const api = useTrueBizApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [exportTableFormModalOpen, setExportTableFormModalOpen] =
    useState<boolean>(false);
  const [notifications, setNotifications] =
    useState<APIPagedNotifications | null>(null);
  const { page: pageString = "1" } = Object.fromEntries(searchParams);
  const page = parseInt(pageString, 10);

  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const offset = (page - 1) * RESULTS_PER_PAGE;
        const notifications = await api.getPendingNotifications(
          offset,
          RESULTS_PER_PAGE,
          [
            APINotificationType.CustomerReviews,
            APINotificationType.IndustryClassification,
            APINotificationType.People,
            APINotificationType.SiteHealth,
            APINotificationType.SiteTraffic,
            APINotificationType.SiteContent,
          ],
          [
            // dev only
            APIMonitorType.Dummy,
            APIMonitorType.HelloWorld,

            APIMonitorType.WebPresence,
            APIMonitorType.WebsiteStatus,

            // Intentionally exclude APIMonitorType.WebsiteContent here
          ]
        );
        setPendingNotificationCount(notifications.count);
        setNotifications(notifications);
      } finally {
        setLoading(false);
      }
    };

    loadNotifications();
  }, [api, page, pendingNotificationCount, setPendingNotificationCount]);

  const setPage = (page: number) => {
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: String(page),
    });
  };

  return (
    <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          <Typography component="h2" fontWeight={700}>
            Unhandled Monitoring Issues
          </Typography>
        </Box>

        <Box sx={{ display: "flex" }}>
          <Button
            variant="outlined"
            disabled={loading}
            onClick={() => {
              setExportTableFormModalOpen(!exportTableFormModalOpen);
            }}
          >
            Export
          </Button>
          <NotificationsExportForm
            open={exportTableFormModalOpen}
            onClose={() => {
              setExportTableFormModalOpen(false);
            }}
            onSubmit={() => {
              setExportTableFormModalOpen(false);
            }}
          />
        </Box>
      </Box>

      {loading ? (
        <CircularProgress size={28} sx={{ marginLeft: 1 }} />
      ) : (
        <>
          <TableContainer component={Box} sx={{ marginTop: 3 }}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>{/* Blank for now */}</TableCell>

                  <TableCell>
                    <strong>Domain</strong>
                  </TableCell>

                  <TableCell>
                    <strong>Time</strong>
                  </TableCell>

                  <TableCell>
                    <strong>Message</strong>
                  </TableCell>

                  <TableCell>{/* Blank for now */}</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {(notifications?.items || []).map((item, index) => {
                  return (
                    <MonitoringNotificationRow
                      key={index}
                      notification={item}
                      setPendingNotificationCount={setPendingNotificationCount}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {notifications && notifications.count > RESULTS_PER_PAGE && (
            <Box
              sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}
            >
              <Pagination
                siblingCount={3}
                count={Math.ceil(notifications.count / RESULTS_PER_PAGE)}
                page={page}
                onChange={(_: React.ChangeEvent<unknown>, value: number) => {
                  setPage(value);
                }}
              />
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}

function MonitoringNotificationRow({
  notification,
  setPendingNotificationCount,
}: {
  notification: APINotification;
  setPendingNotificationCount: React.Dispatch<
    React.SetStateAction<number | null>
  >;
}) {
  const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);
  const [dismissalFormOpen, setDismissalFormOpen] =
    React.useState<boolean>(false);

  // We're going to assume that the the object type is "enquiry" for now, since the API call was scoped
  // to types that would return enquiries. In the fuuture, this may change.
  const enquiryID = notification.resource_id;

  return (
    <React.Fragment key={notification.id}>
      <TableRow
        sx={{
          "&:last-child td, &:last-child th": { border: 0 },
        }}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setDrawerOpen(!drawerOpen)}
          >
            {drawerOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          <Typography fontWeight={600}>
            {notification.subject_urn?.startsWith("urn:domain")
              ? notification.subject_urn.split(":")[2]
              : "--"}
          </Typography>
        </TableCell>
        <TableCell component="th" scope="row">
          <Typography>
            {tzAwareHumanizeTimeString(notification.created_at)}
          </Typography>
        </TableCell>
        <TableCell component="th" scope="row">
          <Typography>{notification.messages?.join(" ")}</Typography>
        </TableCell>
        <TableCell component="th" scope="row">
          <Button onClick={() => setDismissalFormOpen(!dismissalFormOpen)}>
            Dismiss
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={drawerOpen} timeout="auto" unmountOnExit>
            {enquiryID && <EnquiryResultContent enquiryID={enquiryID} />}
          </Collapse>
        </TableCell>
      </TableRow>
      <NotificationDismissalForm
        notificationId={notification.id}
        open={dismissalFormOpen}
        onClose={() => {
          setDismissalFormOpen(false);
        }}
        onSubmit={() => {
          setPendingNotificationCount(null);
          setDismissalFormOpen(false);
        }}
      />
    </React.Fragment>
  );
}

function EnquiryResultContent({ enquiryID }: { enquiryID: string }) {
  const api = useTrueBizApi();
  // We need to figure out what kind of enquiry this is based on its monitor type
  // and then render the appropriate component
  const [enquiry, setEnquiry] = useState<APIEnquiry | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadEnquiry = async () => {
      try {
        setEnquiry(await api.getEnquiry(enquiryID));
      } finally {
        setLoading(false);
      }
    };

    loadEnquiry();
  }, [api, enquiryID]);

  if (enquiry && enquiry.monitor_type === "website_status_monitor") {
    return <WebsiteStatusResult enquiry={enquiry} />;
  }
  if (enquiry && enquiry.monitor_type === "web_presence_monitor") {
    return <WebPresenceResult enquiry={enquiry} />;
  }

  return (
    <React.Fragment key="status_stuff">
      {loading && <CircularProgress size={28} sx={{ marginLeft: 1 }} />}
      {!loading && <div>{`No data to display. ${enquiry?.monitor_type}`}</div>}
    </React.Fragment>
  );
}
